import React, { useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import { DialogContent, DialogTitle, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Heading from "../../../../component-lib/Atoms/heading/EventEHRHeading";
import RxOrderForm from "../../../../forms/RxOrderForm";
import XRayOrderForm from "../../../../forms/XRayOrderForm";
import GenericOrderForm from "../../../../forms/GenericOrderForm";
import { TEXT_CONSTANTS } from "../../../../utils/text-constants/textConstants";
import { canOrderXRay } from "../../../../utils/careType/canOrderXRay";
import { isXRayName } from "../../../../utils/careType/isXRayName";
import { useParams } from "react-router-dom";
import moment from "moment";
import EventEHRGroupedSelect from "../../../../component-lib/Atoms/Select/EventEHRGroupSelect";
import { useDispatch, useSelector } from "react-redux";
import { postOrderData } from "../../patientSaga";
import { componentKey } from "../../patientSlice";
import PatientListService from "../../../../services/PatientListService";
import { fetchAllPages } from "../../../../utils/fetchAllPages";

type ApiOrderTypeOption = {
  type: "item";
  label: string;
  value: string;
  subheaderOption: true;
  name: string;
  isItemRequired: boolean;
  isNoteRequired: boolean;
  isXRay: boolean;
};

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(3),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
  "& .MuiPaper-root": {
    width: "100%", // Set your custom width here
  },
}));

interface CreateOrderDialogProps {
  open: boolean;
  defaultSelectedOrderType?: string;
  handleCloseDialog: () => void;
}

export default function CreateOrderDialog({
  open = false,
  defaultSelectedOrderType,
  handleCloseDialog,
}: CreateOrderDialogProps) {
  const { visitNoteCareFlowData, notesData } = useSelector(
    (state: any) => state[componentKey]
  );

  const [apiOrderTypes, setApiOrderTypes] = useState<ApiOrderTypeOption[]>([]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        // Fetch ALL order types (page through everything) so the dropdown is
        // never truncated by the page limit. The list is scoped to the current
        // facility by the request interceptor (facilityId on every GET).
        const list = await fetchAllPages(
          PatientListService.getOrderTypes,
          (res) => res?.data?.orderTypes,
        );
        if (cancelled) return;
        setApiOrderTypes(
          list.map((o: any) => ({
              type: "item" as const,
              label: o.name,
              value: o.id,
              subheaderOption: true as const,
              name: o.name,
              isItemRequired: !!o.isItemRequired,
              isNoteRequired: !!o.isNoteRequired,
              isXRay: isXRayName(o.name),
            }))
        );
      } catch {
        // AppDataService interceptor surfaces the toast
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  // X-Ray comes from master data (facility-scoped) but is orderable only for
  // Physician Care / Orthopedic Physician Care — hide the master-data X-Ray
  // order type for any other care type.
  const showXRay = canOrderXRay(visitNoteCareFlowData?.typeOfCare);
  const visibleApiOrderTypes = apiOrderTypes.filter(
    (o) => !o.isXRay || showXRay
  );

  const orderTypeOptions: any[] = [
    { type: "item", label: "Rx Order", value: "rxOrder" },
    { type: "subheader", label: "Non RX Order" },
    ...visibleApiOrderTypes,
  ];

  let index = visitNoteCareFlowData?.visit_note?.findIndex(
    (obj: any) => obj.id === notesData.id
  );
  // Set index to last  element if not found

  if (index === -1) {
    index = visitNoteCareFlowData?.visit_note?.length - 1;
  }

  const CUSTOM_lABEL_STYLE = {
    fontSize: "16px",
    fontWeight: 500,
    color: "#393939",
    marginBottom: "10px",
  };
  const dispatch = useDispatch();
  const { visitNoteId, patientId } = useParams();

  const [orderType, setOrderType] = useState(
    defaultSelectedOrderType || "rxOrder"
  );

  // const deviceType = useDeviceType();

  const handleClose = () => {
    handleCloseDialog();
  };

  const handleSubmittedOrderData = (
    data: any,
    type: string,
    picked?: ApiOrderTypeOption
  ) => {
    const items = data?.items?.map((d: any) => ({ name: d?.item })) ?? [];

    let payload = {};
    if (type === "rxOrder") {
      payload = {
        patientId: patientId,
        visitNoteId: visitNoteCareFlowData?.visit_note?.[index]?.id,
        type: "Rx Order",
        item: [
          {
            id: data?.medicationName,
            medicationName: data?.medicationTitle,
            quantity: data?.qty,
            unit: data?.unit,
          },
        ],
        frequency: data?.frequency,
        note: data?.notes,
      };
    } else if (type === "xRayOrder") {
      payload = {
        patientId: patientId,
        type: "X_Ray",
        item: [{ name: data?.xRayInfo }],
        visitNoteId: visitNoteCareFlowData?.visit_note?.[index]?.id,
        note: data?.notes,
      };
    } else if (type === "apiItem" && picked) {
      payload = {
        patientId: patientId,
        visitNoteId: visitNoteCareFlowData?.visit_note?.[index]?.id,
        type: picked.name,
        item: picked.isItemRequired ? items : [],
        note: picked.isNoteRequired ? data?.notes : undefined,
      };
    }

    dispatch(postOrderData(payload));
  };

  return (
    <React.Fragment>
      <BootstrapDialog
        // onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={open}
      >
        <div className="h-full flex flex-col justify-between">
          <DialogTitle
            sx={{ m: 0, p: 2, fontWeight: 600, fontSize: "18px" }}
            id="customized-dialog-title"
          >
            {TEXT_CONSTANTS?.CREATE_ORDER}
          </DialogTitle>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={(theme) => ({
              position: "absolute",
              right: 8,
              top: 8,
              color: theme.palette.grey[500],
            })}
          >
            <CloseIcon />
          </IconButton>
          <hr />
          {/* MAIN CONTENT */}
          <DialogContent
            sx={{
              height: "fit-content",
            }}
          >
            <div className="flex flex-col gap-6">
              {/* ORDER TYPE DROPDOWN */}
              <div>
                <Heading
                  title={TEXT_CONSTANTS?.ORDER_TYPE}
                  customStyle={CUSTOM_lABEL_STYLE}
                />
                <EventEHRGroupedSelect
                  label=""
                  id="grouped-select"
                  options={orderTypeOptions}
                  defaultValue={orderType}
                  onChange={(e) => {
                    setOrderType(e.target.value);
                  }}
                />
              </div>

              {/* RX ORDER */}
              {orderType === "rxOrder" && (
                <RxOrderForm
                  getFormData={(data: any) => {
                    handleSubmittedOrderData(data, "rxOrder");
                  }}
                />
              )}

              {/* API-DRIVEN ORDER (X-Ray uses the dedicated form) */}
              {(() => {
                const picked = apiOrderTypes.find(
                  (o) => o.value === orderType
                );
                if (!picked) return null;
                if (picked.isXRay) {
                  return (
                    <XRayOrderForm
                      key={picked.value}
                      getFormData={(data: any) => {
                        handleSubmittedOrderData(data, "xRayOrder", picked);
                      }}
                    />
                  );
                }
                return (
                  <GenericOrderForm
                    key={picked.value}
                    isItemRequired={picked.isItemRequired}
                    isNoteRequired={picked.isNoteRequired}
                    getFormData={(data) => {
                      handleSubmittedOrderData(data, "apiItem", picked);
                    }}
                  />
                );
              })()}
            </div>
          </DialogContent>
        </div>
      </BootstrapDialog>
    </React.Fragment>
  );
}
